import React from 'react';
import { Icon } from '@hydrodigital/design-system';
import type { SensorDevice } from '../types/sensorMaintenanceLens.types';

interface SensorDeviceModalProps {
	device: SensorDevice;
	onClose: () => void;
}

function resolveImageSource(imageBase64: string): string | null {
	if (!imageBase64) return null;
	if (imageBase64.startsWith('data:')) return imageBase64;
	return `data:image/jpeg;base64,${imageBase64}`;
}

function formatCoordinates(latitude: number, longitude: number): string | null {
	if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
	const latDirection = latitude >= 0 ? 'N' : 'S';
	const lngDirection = longitude >= 0 ? 'E' : 'W';
	return `${Math.abs(latitude).toFixed(4)}° ${latDirection}, ${Math.abs(longitude).toFixed(4)}° ${lngDirection}`;
}

const SensorDeviceModal: React.FC<SensorDeviceModalProps> = ({ device, onClose }) => {
	const imageSource = resolveImageSource(device.imageBase64);
	const coordinates = formatCoordinates(device.latitude, device.longitude);

	const rows: { label: string; value: string }[] = [
		{ label: 'Sensor Type', value: device.sensorType || 'NA' },
		{ label: 'RTU Model', value: device.rtuModel || 'NA' },
		{ label: 'Throughput (last 24h)', value: device.throughput ?? 'NA' },
		{ label: 'Last serviced', value: device.lastServiced || 'NA' },
		{ label: 'Last reported', value: device.lastReported || device.lastDataReceived || 'NA' },
		...device.sensorValues.map((sensor) => ({ label: sensor.name, value: sensor.value || 'NA' })),
	];

	return (
		<div className="bg-white rounded-2xl shadow-lg p-5 w-[780px] max-w-full font-sans">
			<div className="flex items-start justify-between gap-4 mb-2">
				<div className="flex flex-col gap-0.5 min-w-0">
					<h5 className="text-xl font-semibold text-[#232B36] m-0 leading-tight break-words">
						{device.deviceId}
					</h5>
					<span className="text-sm font-medium text-[#74788D]">{device.deviceType || 'NA'}</span>
				</div>
				<button
					type="button"
					onClick={onClose}
					aria-label="Close"
					className="flex-none flex items-center justify-center w-8 h-8 -mt-1 -mr-1 rounded-full text-[#74788D] hover:bg-[#F1F3F7] transition-colors"
				>
					<Icon name="exit" size="m" />
				</button>
			</div>
			<div className="grid grid-cols-2 gap-5 items-start">
				<div className="flex flex-col rounded-xl border border-[#E5E9F0] overflow-hidden divide-y divide-[#E5E9F0]">
					{rows.map((row, index) => (
						<div
							key={`${row.label}-${index}`}
							className="flex items-center justify-between gap-3 px-4 py-2"
						>
							<span className="text-sm font-medium text-[#74788D] whitespace-nowrap">{row.label}</span>
							<span className="text-sm font-semibold text-[#232B36] text-right break-words">
								{row.value}
							</span>
						</div>
					))}
				</div>

				<div className="flex flex-col gap-2">
					{imageSource ? (
						<img
							src={imageSource}
							alt={`${device.deviceId} site`}
							className="w-full h-[220px] object-cover rounded-xl"
						/>
					) : (
						<div className="w-full h-[220px] rounded-xl bg-[#F1F3F7] flex items-center justify-center">
							<Icon name="image" size="l" />
						</div>
					)}
					{coordinates && (
						<div className="flex items-center gap-1.5 text-sm font-medium text-[#2E93FA]">
							<Icon name="map-marker" size="s" />
							<span className="underline break-words">{coordinates}</span>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default SensorDeviceModal;
